import React, { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormField } from "components/ui/form";
import { InputGs, SearchableMultiSelectGs, SearchableSelectGs } from "components/ui/Forms";
import DatePickerGs from "components/ui/Forms/datePickerGs";
import { Button } from "components/ui/ButtonGs";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useErrorHandler } from "exceptions/ErrorHandler";
import { formSchema } from "../../formSchema";
import { useHubDriver, useCities, useHubCarrier, useDriver } from "hooks/api";
import { useHistory } from "react-router-dom";
import { customMask } from "lib/customMask";
import { toast } from "sonner";
import { format } from "date-fns";
import { listStatesOptions } from "utils/data/states";
import { RootStateOrAny, useSelector } from "react-redux";
import { AuthState } from "store/ducks/auth/auth";

export const CreateForm: React.FC = () => {
  const navigate = useHistory();
  const queryClient = useQueryClient();
  const { errorHandler } = useErrorHandler();
  const { createDriver } = useHubDriver();
  const { getCities, getCEP } = useCities();
  const { findDriver } = useDriver();
  const { fetchCarriers } = useHubCarrier();

  // Estado selecionado
  const [selectedState, setSelectedState] = React.useState<string>("");
  const [selectedCity, setSelectedCity] = React.useState<string>();
  const [cep, setCep] = React.useState<string | null>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const onSuccessCEP = useCallback(
    (data: any) => {
      form.setValue("address_street", data.address_street || "");
      form.setValue("address_neighborhood", data.address_neighborhood || "");
      form.setValue("address_city", data.address_city || "");
      form.setValue("address_state", data.address_state || "");
      form.setValue("address_id_city_ibge", data.address_id_city_ibge || "");
      setSelectedState(data.address_state);
      setSelectedCity(data.address_city);
    },
    [form]
  );

  const {
    data: dataCities,
    isLoading: isLoadingCities,
    refetch: refetchCities,
  } = useQuery({
    queryKey: ["getCities", selectedState],
    queryFn: () => getCities({ state: selectedState }),
    enabled: false,
  });

  const mutationFindDriver = useMutation(findDriver, {
    onSuccess: (data) => {
      form.setValue("name", data?.name || "");
      if (data?.birth_date) {
        const dt = new Date(data.birth_date);
        form.setValue("birth_date", dt || undefined);
      }
    },
    onError: () => {
      form.resetField("name");
      form.resetField("birth_date");
    },
  });

  const { isLoading: isLoadingCEP, refetch: refetchCEP } = useQuery({
    queryKey: ["getCEP", cep],
    queryFn: () => getCEP(cep),
    enabled: false,
    onSuccess: (response: any) => {
      setCep(() => null);
      onSuccessCEP(response);
      return response;
    },
  });

  const { data: carriers, isLoading: loadinDocTypes } = useQuery({
    queryKey: ["fetchCarriers"],
    queryFn: () => fetchCarriers(),
    staleTime: 1000 * 60 * 5, // 5 min,
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation(createDriver, {
    onSuccess: () => {
      queryClient.invalidateQueries("createDriver");
      navigate.push("/settings/hub-drivers");
      toast.success("Motorista criada com sucesso!");
    },
    onError: (error) => {
      errorHandler(error);
    },
  });

  const auth = useSelector<RootStateOrAny>(
    (state) => state.auth
  ) as AuthState;

  const isAdmin = auth.data?.is_super_adm || auth.data?.role === "Administrador";

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (isAdmin && !data.carrier_ids?.length) {
      form.setError('carrier_ids', {
        type: 'manual',
        message: 'Selecione pelo menos uma transportadora',
      });

      return;
    }

    if (!isAdmin && !data.carrier_id) {
      form.setError('carrier_id', {
        type: 'manual',
        message: 'Selecione a transportadora',
      });

      return;
    }

    const cleanDocument = data.document?.replace(/[^\d]/g, '') || '';
    const birth_date = format(new Date(data.birth_date), 'yyyy-MM-dd');

    mutation.mutate({
      ...data,
      carrier_id: isAdmin ? undefined : data.carrier_id,
      carrier_ids: isAdmin ? data.carrier_ids : undefined,
      document: cleanDocument,
      birth_date,
    });
  };

  const handleCityChange = useCallback((state: string) => {
    setSelectedState(() => state);
  }, []);

  const handleDocumentChange = useCallback((document: string) => {
    const cleanDocument = document?.replace(/[^\d]/g, "") || "";
    if (cleanDocument.length === 11) {
      mutationFindDriver.mutate(cleanDocument);
    }
  }, []);

  const handleCepChange = useCallback((cep: string) => {
    const cleanCEP = cep ? cep.replace(/[^0-9]/g, "") : null;

    if (cleanCEP && cleanCEP.length === 8) {
      setCep(cleanCEP);
    } else {
      setCep(null);
      toast.warning("CEP inválido!");
    }
  }, []);

  useEffect(() => {
    if (selectedState) {
      refetchCities();
    }
  }, [selectedState, refetchCities]);

  useEffect(() => {
    if (cep) {
      refetchCEP();
    }
  }, [cep, refetchCEP]);

  useEffect(() => {
    if (selectedCity && dataCities) {
      form.setValue("address_city", selectedCity || "");
    }
  }, [selectedCity, dataCities, form]);

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className={isAdmin ? "md:col-span-2" : ""}>
              {isAdmin ? (
                <FormField
                  name="carrier_ids"
                  control={form.control}
                  render={({ field }) => (
                    <SearchableMultiSelectGs
                      label="Transportadora"
                      content={carriers?.comboOptions || []}
                      isLoading={loadinDocTypes}
                      value={field.value ?? []}
                      onValueChange={(value: string[]) => {
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              ) : (
                <FormField
                  name="carrier_id"
                  control={form.control}
                  render={({ field }) => (
                    <SearchableSelectGs
                      {...field}
                      label="Transportadora"
                      content={carriers?.comboOptions || []}
                      isLoading={loadinDocTypes}
                      value={field.value ?? undefined}
                      onValueChange={(value: string) => {
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              )}
            </div>

            <FormField
              name="document"
              control={form.control}
              render={({ field }) => (
                <InputGs
                  {...field}
                  label="CPF"
                  value={field.value ?? ""}
                  onChange={(e) => {
                    field.onChange(
                      customMask(e.target.value, "999.999.999-99")
                    );
                    handleDocumentChange(e.target.value);
                  }}
                />
              )}
            />

            <FormField
              control={form.control}
              name="birth_date"
              render={({ field }) => (
                <DatePickerGs
                  {...field}
                  label={"Data de Nascimento"}
                  fieldValue={field.value}
                  fieldOnChange={field.onChange}
                  showMonthYearPicker
                  isLoading={mutationFindDriver.isLoading}
                />
              )}
            />

            <FormField
              name="name"
              control={form.control}
              render={({ field }) => (
                <InputGs
                  {...field}
                  label="Nome"
                  value={field.value ?? ""}
                  isLoading={mutationFindDriver.isLoading}
                />
              )}
            />

            <FormField
              name="phone_number"
              control={form.control}
              render={({ field }) => (
                <InputGs
                  {...field}
                  label="Telefone"
                  value={field.value ?? ""}
                  onChange={(e) =>
                    field.onChange(
                      customMask(e.target.value, "(99) 99999-9999")
                    )
                  }
                />
              )}
            />
            {/* <FormField
              name="email"
              control={form.control}
              render={({ field }) => (
                <InputGs {...field} value={field.value ?? ""} label="Email" />
              )}
            /> */}
            <FormField
              name="address_zipcode"
              control={form.control}
              render={({ field }) => (
                <InputGs
                  {...field}
                  value={field.value ?? ""}
                  label="CEP"
                  onBlur={(e: any) => handleCepChange(e.target.value)}
                  onChange={(e) =>
                    field.onChange(customMask(e.target.value, "99999-999"))
                  }
                />
              )}
            />

            <FormField
              name="address_state"
              control={form.control}
              render={({ field }) => (
                <SearchableSelectGs
                  {...field}
                  label={"UF"}
                  onValueChange={(value: string) => {
                    field.onChange(value);
                    handleCityChange(value);
                  }}
                  content={listStatesOptions || []}
                  isLoading={isLoadingCEP}
                  value={field.value ?? undefined}
                />
              )}
            />
            <FormField
              name="address_city"
              control={form.control}
              render={({ field }) => (
                <SearchableSelectGs
                  {...field}
                  label={"Cidade"}
                  onValueChange={field.onChange}
                  content={dataCities || []}
                  isLoading={isLoadingCities || isLoadingCEP}
                  value={field.value ?? undefined}
                />
              )}
            />
            <FormField
              name="address_id_city_ibge"
              control={form.control}
              render={({ field }) => (
                <InputGs
                  {...field}
                  value={field.value ?? ""}
                  label="IBGE"
                  hidden
                />
              )}
            />
            <FormField
              name="address_street"
              control={form.control}
              render={({ field }) => (
                <InputGs
                  {...field}
                  value={field.value ?? ""}
                  label="Logradouro"
                  isLoading={isLoadingCEP}
                />
              )}
            />
            <FormField
              name="address_number"
              control={form.control}
              render={({ field }) => (
                <InputGs
                  {...field}
                  value={field.value ?? ""}
                  label="Número"
                  isLoading={isLoadingCEP}
                />
              )}
            />
            <FormField
              name="address_neighborhood"
              control={form.control}
              render={({ field }) => (
                <InputGs
                  {...field}
                  value={field.value ?? ""}
                  label="Bairro"
                  isLoading={isLoadingCEP}
                />
              )}
            />
          </div>
          <div className="flex justify-end w-full gap-4">
            <Button.Root
              variant="secondary"
              size="sm"
              type="button"
              onClick={() => navigate.push("/settings/hub-drivers")}
            >
              Cancelar
            </Button.Root>
            <Button.Root
              variant="primary"
              size="sm"
              type="submit"
              disabled={mutation.isLoading}
            >
              Salvar
            </Button.Root>
          </div>
        </form>
      </Form>
    </div>
  );
};
